import api from './api';
import { ApiResponse } from '../types';
import {
  FinePayment,
  FinePaymentsListResponse,
  FinePaymentsSummary,
  RecordPaymentPayload,
  FinePaymentMethod,
  LetterCategory,
  LettersListResponse,
  LetterPaymentStatus, FinePaymentDetail } from '../types/letters';

const buildQueryString = (params: Record<string, string | number | undefined | null>): string => {
  const search = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v != null && v !== '') search.append(k, String(v));
  }
  return search.toString();
};

export interface FinePaymentsListFilters {
  from?: string;
  to?: string;
  method?: FinePaymentMethod | 'all';
  studentId?: string;
  collectedBy?: string;
  page?: number;
  limit?: number;
}

const listLetterPayments = async (letterId: string): Promise<FinePayment[]> => {
  const res = await api.get<ApiResponse<FinePayment[]>>(`/letters/${letterId}/payments`);
  if (!res.data.success) throw new Error(res.data.message);
  return res.data.data || [];
};

const recordPaymentForLetter = async (letterId: string, payload: RecordPaymentPayload): Promise<FinePayment> => {
  const res = await api.post<ApiResponse<FinePayment>>(`/letters/${letterId}/payments`, payload);
  if (!res.data.success) throw new Error(res.data.message);
  return res.data.data!;
};

const listFinePayments = async (filters: FinePaymentsListFilters = {}): Promise<FinePaymentsListResponse> => {
  const qs = buildQueryString({
    from: filters.from,
    to: filters.to,
    method: filters.method === 'all' ? undefined : filters.method,
    studentId: filters.studentId,
    collectedBy: filters.collectedBy,
    page: filters.page,
    limit: filters.limit
  });
  const res = await api.get<ApiResponse<FinePaymentsListResponse>>(`/fine-payments?${qs}`);
  if (!res.data.success) throw new Error(res.data.message);
  return res.data.data!;
};

const getFinePaymentsSummary = async (): Promise<FinePaymentsSummary> => {
  const res = await api.get<ApiResponse<FinePaymentsSummary>>('/fine-payments/summary');
  if (!res.data.success) throw new Error(res.data.message);
  return res.data.data!;
};

const reverseFinePayment = async (id: string, reason: string): Promise<void> => {
  const res = await api.post<ApiResponse<void>>(`/fine-payments/${id}/reverse`, { reason });
  if (!res.data.success) throw new Error(res.data.message);
};

export interface OutstandingFinesFilters {
  category?: string;
  paymentStatus?: LetterPaymentStatus | 'all';
  from?: string;
  to?: string;
  /** Student name, title or reason — searched across every outstanding fine. */
  search?: string;
  page?: number;
  limit?: number;
}

/** Outstanding fines page + what is owed across the whole filtered set. */
export interface OutstandingFinesResponse extends LettersListResponse {
  totals: { outstanding: number };
}

const getFinesContext = async (): Promise<{ categories: LetterCategory[] }> => {
  const res = await api.get<ApiResponse<{ categories: LetterCategory[] }>>('/fine-payments/context');
  if (!res.data.success) throw new Error(res.data.message);
  return res.data.data!;
};

const listOutstandingFines = async (filters: OutstandingFinesFilters = {}): Promise<OutstandingFinesResponse> => {
  const qs = buildQueryString({
    category: filters.category,
    paymentStatus: filters.paymentStatus === 'all' ? undefined : filters.paymentStatus,
    from: filters.from,
    to: filters.to,
    search: filters.search?.trim() || undefined,
    page: filters.page,
    limit: filters.limit
  });
  const res = await api.get<ApiResponse<OutstandingFinesResponse>>(`/fine-payments/outstanding?${qs}`);
  if (!res.data.success) throw new Error(res.data.message);
  return res.data.data!;
};

/** One fine payment with what its receipt prints (fine, student, collector). */
const getFinePayment = async (id: string): Promise<FinePaymentDetail> => {
  const res = await api.get<ApiResponse<FinePaymentDetail>>(`/fine-payments/${id}`);
  if (!res.data.success) throw new Error(res.data.message);
  return res.data.data!;
};

const finePaymentService = {
  getFinePayment,
  listLetterPayments,
  recordPaymentForLetter,
  listFinePayments,
  getFinePaymentsSummary,
  reverseFinePayment,
  getFinesContext,
  listOutstandingFines
};

export default finePaymentService;
